import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const GovernmentLanding = () => {
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'pageview',
      page: '/government',
      audience: 'Government'
    });
  }, []);

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>URGAA for Government & Municipalities - EV Infrastructure Planning & Monitoring | Go4Garage</title>
        <meta name="title" content="URGAA for Government & Municipalities - EV Infrastructure Planning & Monitoring" />
        <meta name="description" content="Comprehensive EV charging infrastructure planning, monitoring, and compliance platform for government bodies. Track FAME-II targets, monitor private CPOs, ensure EV policy compliance across your jurisdiction." />
        <meta name="keywords" content="government EV infrastructure, municipality charging stations, FAME II monitoring, EV policy compliance, smart city EV planning, public charging infrastructure India" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://urgaa.in/government" />
        <meta property="og:title" content="URGAA for Government & Municipalities" />
        <meta property="og:description" content="Comprehensive EV charging infrastructure planning, monitoring, and compliance platform for government bodies." />
        <meta property="og:image" content="https://urgaa.in/images/og-government.jpg" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://urgaa.in/government" />
        <meta name="twitter:title" content="URGAA for Government & Municipalities" />
        <meta name="twitter:description" content="EV infrastructure planning and monitoring platform for government bodies" />
        <meta name="twitter:image" content="https://urgaa.in/images/twitter-government.jpg" />
        <meta name="twitter:site" content="@Go4Garage" />
        
        {/* Additional Meta */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://urgaa.in/government" />
        
        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "GovernmentService",
            "name": "URGAA for Government",
            "serviceType": "EV Infrastructure Planning & Monitoring",
            "provider": {
              "@type": "Organization",
              "name": "Go4Garage Private Limited",
              "url": "https://urgaa.in"
            },
            "areaServed": "IN",
            "description": "Comprehensive EV charging infrastructure planning and monitoring platform for government bodies"
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        {/* Hero Section */}
        <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-6">
              Trusted by 15+ Municipalities | FAME-II Compliant
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Accelerate Your City's<br />EV Infrastructure Goals
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Plan, monitor, and govern EV charging infrastructure across your jurisdiction. From DSAT site intelligence to CPO compliance tracking.
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/login" className="px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
                Request Demo
              </Link>
              <a href="#features" className="px-8 py-4 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 font-semibold">
                Learn More
              </a>
            </div>
          </div>
        </section>

        {/* Challenges Section */}
        <section className="py-16 bg-orange-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Challenges in Public EV Infrastructure</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-orange-600 text-4xl mb-4">🎯</div>
                <h3 className="text-xl font-bold mb-2">FAME-II Target Gaps</h3>
                <p className="text-gray-600">Most cities are behind on 2024 EV charging targets. Need data-driven site selection.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-orange-600 text-4xl mb-4">👁️</div>
                <h3 className="text-xl font-bold mb-2">Zero Visibility</h3>
                <p className="text-gray-600">No unified dashboard to monitor private CPOs operating in your jurisdiction.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-orange-600 text-4xl mb-4">📋</div>
                <h3 className="text-xl font-bold mb-2">Compliance Chaos</h3>
                <p className="text-gray-600">Manual tracking of EV Yatra Portal submissions, land permissions, and safety audits.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section id="features" className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">URGAA Government Edition</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border-2 border-blue-500 p-6 rounded-lg">
                <div className="text-blue-600 text-4xl mb-4">🗺️</div>
                <h3 className="text-xl font-bold mb-2">DSAT-Powered Planning</h3>
                <p className="text-gray-600 mb-4">AI recommends optimal locations for public charging stations based on demand patterns</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Heat maps of EV demand by ward/zone</li>
                  <li>✓ Gap analysis (supply vs demand)</li>
                  <li>✓ ROI projections for public investments</li>
                  <li>✓ Integration with GIS/Master Plan data</li>
                </ul>
              </div>
              <div className="border-2 border-green-500 p-6 rounded-lg">
                <div className="text-green-600 text-4xl mb-4">📊</div>
                <h3 className="text-xl font-bold mb-2">Real-Time Monitoring Dashboard</h3>
                <p className="text-gray-600 mb-4">Live view of all charging stations (public & private) in your jurisdiction</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Network health by zone/ward</li>
                  <li>✓ Uptime tracking for public stations</li>
                  <li>✓ Usage statistics (sessions, kWh, revenue)</li>
                  <li>✓ CPO-wise performance comparison</li>
                </ul>
              </div>
              <div className="border-2 border-purple-500 p-6 rounded-lg">
                <div className="text-purple-600 text-4xl mb-4">✅</div>
                <h3 className="text-xl font-bold mb-2">Compliance Tracking</h3>
                <p className="text-gray-600 mb-4">Automated monitoring of regulatory compliance across all CPOs</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ EV Yatra Portal sync status</li>
                  <li>✓ Land permission tracking</li>
                  <li>✓ Safety audit deadlines</li>
                  <li>✓ Pricing regulation compliance</li>
                </ul>
              </div>
              <div className="border-2 border-indigo-500 p-6 rounded-lg">
                <div className="text-indigo-600 text-4xl mb-4">📈</div>
                <h3 className="text-xl font-bold mb-2">Progress Reports</h3>
                <p className="text-gray-600 mb-4">Auto-generated reports for FAME-II monitoring and policy reviews</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Monthly infrastructure growth reports</li>
                  <li>✓ Quarterly FAME-II target tracking</li>
                  <li>✓ Annual ESG impact assessments</li>
                  <li>✓ Export to PDF for council presentations</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">Special Government Pricing</h2>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="text-5xl font-bold text-blue-600 mb-2">Custom Pricing</div>
              <div className="text-gray-600 mb-6">Based on jurisdiction size and features</div>
              <ul className="text-left space-y-3 mb-8 max-w-md mx-auto">
                <li>✓ Tiered pricing by population/area</li>
                <li>✓ Read-only access for unlimited officials</li>
                <li>✓ Integration with existing GIS/Smart City systems</li>
                <li>✓ Dedicated account manager</li>
                <li>✓ On-premise deployment option</li>
                <li>✓ Annual maintenance & training included</li>
              </ul>
              <Link to="/login" className="w-full block py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
                Request Detailed Proposal
              </Link>
              <p className="text-sm text-gray-500 mt-4">GeM registration available | Pilot projects welcomed</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-blue-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-4">Ready to Build Smart EV Infrastructure?</h2>
            <p className="text-xl mb-8">Join forward-thinking municipalities already using URGAA</p>
            <Link to="/login" className="px-8 py-4 bg-white text-blue-600 rounded-lg hover:bg-gray-100 font-semibold inline-block">
              Schedule Consultation →
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default GovernmentLanding;